import { Geolocation } from '@capacitor/geolocation';
import { mande } from 'mande';
import { defineStore } from 'pinia';
import { fetchWithTimeoutAndCache, loadCache, saveCacheArray } from '@/utils/cacheUtils.js';

import { auth0 } from '../auth0.js';

export const initApi = async () => {
  const token = await auth0.getAccessTokenSilently();

  return mande(`${import.meta.env.VITE_APP_API_URL}/assets`, {
    headers: {
      Authorization: `Bearer ${token}`
    }
  });
};

type LocationStatus = 'idle' | 'loading' | 'ok' | 'denied' | 'timeout' | 'unavailable';

const GEOLOCATION_TIMEOUT_MS = 8000;
const GEOLOCATION_MAX_AGE_MS = 60000;
// W3C GeolocationPositionError codes
const GEOLOCATION_ERROR_PERMISSION_DENIED = 1;
const GEOLOCATION_ERROR_TIMEOUT = 3;

const parseCoord = (v: unknown): number | null => {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
};

const getDistance = (lat1: number, lon1: number, lat2: number, lon2: number) => {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const d = R * c;

  // Convert km to miles
  return d * 0.621371;
};

export const useStore = defineStore('asset', {
  // arrow function recommended for full type inference
  state: (): {
    assets: ETNAsset[];
    location: { lat?: number; lng?: number };
    locationStatus: LocationStatus;
  } => {
    return {
      assets: [],
      location: {},
      locationStatus: 'idle'
    };
  },
  actions: {
    async getCurrentLocation() {
      if (this.locationStatus === 'loading') return null;
      const hadFix = this.locationStatus === 'ok';
      this.locationStatus = 'loading';
      try {
        const pos = await Geolocation.getCurrentPosition({
          timeout: GEOLOCATION_TIMEOUT_MS,
          maximumAge: GEOLOCATION_MAX_AGE_MS
        });
        this.location = {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude
        };
        this.locationStatus = 'ok';
        return this.location;
      } catch (e) {
        if (hadFix) {
          // Users routinely move from signal to no-signal mid-session
          // (e.g. on-site readings); keep the last-known fix so the
          // dropdown stays distance-sorted instead of snapping back
          // to alphabetical.
          this.locationStatus = 'ok';
        } else {
          const code = (e as { code?: number })?.code;
          if (code === GEOLOCATION_ERROR_PERMISSION_DENIED) this.locationStatus = 'denied';
          else if (code === GEOLOCATION_ERROR_TIMEOUT) this.locationStatus = 'timeout';
          else this.locationStatus = 'unavailable';
        }
        console.log('Error getting location', e);
        return null;
      }
    },

    async listAssets() {
      try {
        const fetchFn = async () => {
          const api = await initApi();
          const response = (await api.get({
            query: {
              $limit: 1000000,
              $select: 'siteName,address,addressString'
            }
          })) as ETNAPIResponse;
          return response.data as ETNAsset[];
        };

        const cacheFn = () => loadCache<ETNAsset>('assets');

        const { data: assets, fromCache } = await fetchWithTimeoutAndCache(fetchFn, cacheFn);

        if (!fromCache) {
          await saveCacheArray('assets', assets);
        }

        this.assets = assets;

        await this.getCurrentLocation();

        return this.assets;
      } catch (e) {
        console.error('Failed to load assets:', e);
        this.assets = [];
        return [];
      }
    }
  },
  getters: {
    getAssetById:
      (state) =>
      (assetId: string): ETNAsset | undefined => {
        return state.assets.find((asset) => asset._id === assetId);
      },
    sortedAssets: (state): ETNAsset[] => {
      const { lat, lng } = state.location;
      const hasLocation =
        lat != null && lng != null && Number.isFinite(lat) && Number.isFinite(lng);

      if (!hasLocation) {
        return [...state.assets].sort((a, b) => a.siteName.localeCompare(b.siteName));
      }

      const withDistance = state.assets.map((a) => {
        // Coords are stored as strings in the API (address.schema.js: type: String),
        // and may be empty strings or null on legacy/imported records — guard before
        // coercing so '' and null don't slip through as 0,0.
        const assetLat = parseCoord(a.address.latitude);
        const assetLng = parseCoord(a.address.longitude);
        return {
          ...a,
          distance:
            assetLat != null && assetLng != null
              ? getDistance(lat, lng, assetLat, assetLng)
              : null
        };
      });

      return withDistance.sort((a, b) => {
        if (a.distance == null && b.distance == null) {
          return a.siteName.localeCompare(b.siteName);
        }
        if (a.distance == null) return 1;
        if (b.distance == null) return -1;
        if (a.distance === b.distance) {
          return a.siteName.localeCompare(b.siteName);
        }
        return a.distance - b.distance;
      });
    },
    isDistanceSorted(state): boolean {
      // True only if we got a GPS fix AND at least one asset has usable coords.
      // Otherwise the visible order is alphabetical and the indicator must say so.
      if (state.locationStatus !== 'ok') return false;
      return this.sortedAssets.some((a) => a.distance != null);
    }
  }
});
